// @flow

import _ from 'lodash';

import * as EntityActions from 'app/entity/EntityActions';
import buildEntityActions from 'app/entity/utils/buildEntityActions';
import buildEntityApi from 'app/entity/utils/buildEntityApi';
import buildEntitySelectors from 'app/entity/utils/buildEntitySelectors';
import { APP_STATUSES } from 'app/developer-v3/utils/constants';
import { partialWithDefault } from 'app/common/CommonUtils';
import { putJson } from 'app/common/fetch';

export const type = 'cliApp';

export const READY_TO_ACTIVATE_MESSAGE =
  "You can't promote until we've approved your integration";

const API_BASE = '/api/platform/cli/apps';

export const api = buildEntityApi(type, API_BASE, cliAppApi => {
  cliAppApi.updateApp = (app, id) => {
    return putJson(cliAppApi.modelEndpoint(`${id}`, app));
  };

  cliAppApi.promote = (appId, appVersion, changelog) => {
    return putJson(
      `${API_BASE}/${appId}/versions/${appVersion}/promote/production`,
      { changelog, is_ui: true }
    );
  };
});

export const selectors = buildEntitySelectors(type, sel => {
  sel.activationUrl = _.partial(sel.dataItem, ['']);
  sel.id = _.partial(sel.dataItem, 'id');
  sel.key = _.partial(sel.dataItem, 'key');
  sel.title = _.partial(sel.dataItem, 'title');
  sel.description = _.partial(sel.dataItem, 'description');
  sel.imageUrl = _.partial(sel.dataItem, 'image');
  sel.inviteUrl = _.partial(sel.dataItem, 'invite_url');
  sel.latestVersion = _.partial(sel.dataItem, 'latest_version');
  sel.activeVersions = partialWithDefault(sel.dataItem, ['versions'], []);
  // TODO: EA is going away. Remove earlyAccess and public_ish when we do.
  // For when an app is either public or early_access
  sel.isPublicIsh = _.partial(sel.dataItem, 'public_ish');
  sel.status = entity => _.camelCase(sel.dataItem('status', entity));

  const isStatus = (expected, entity) => sel.status(entity) === expected;
  sel.isPrivate = _.partial(isStatus, APP_STATUSES.private.name);
  sel.isPending = _.partial(isStatus, APP_STATUSES.pending.name);
  sel.isEarlyAccess = _.partial(isStatus, APP_STATUSES.earlyAccess.name);
  sel.isBeta = _.partial(isStatus, APP_STATUSES.beta.name);
  sel.isPublic = _.partial(isStatus, APP_STATUSES.public.name);
  sel.isPublished = entity =>
    sel.isBeta(entity) || sel.isPublic(entity) || sel.isEarlyAccess(entity);

  sel.isReadyToActivate = entity => {
    const [message = ''] = sel.statusMessages(entity) || [];
    return typeof message === 'object'
      ? _.includes(
          _.map(_.flatMap(message.violations, 'results'), 'message'),
          READY_TO_ACTIVATE_MESSAGE
        )
      : message.includes(READY_TO_ACTIVATE_MESSAGE);
  };
  sel.serviceId = _.partial(sel.dataItem, 'service_id');
  // confusingly, 'versions' from the server is only active versions
  // can't change that easily now, but we can be more explicit about what we expect here
  sel.versions = partialWithDefault(sel.dataItem, ['all_versions'], []);
});

export const actions = buildEntityActions(type, selectors, api, appActions => {
  appActions.promoteApp = (appId, appVersion, changelog) =>
    EntityActions.saveEntity(
      type,
      () => {},
      () => api.promote(appId, appVersion, changelog),
      {},
      appId
    );

  appActions.resetStatus = appId =>
    EntityActions.resetEntityStatus(type, appId);
});
